import React from 'react';
import { Link } from 'react-router-dom';
import HtmlContent from '../../../components/common/HtmlContent';
import useHashScroll from '../../../hooks/useHashScroll';

/**
 * PrioritisationPage Component
 * 
 * Displays all problem prioritisation tools content like the legacy HTML site
 */
const PrioritisationPage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();
  
  // Prioritisation intro content from the legacy HTML
  const prioritisationIntroHtml = `
    <p>
    Before progressing on to framing and understanding potential user problems further we need decide which problems to focus on and prioritise based on a number of factors including alignment with business goals, feasibility and urgency. 
    </p>
    
    <p>
    Taking the previously discovered problems and then using them with one or more of the below tools will help with prioritising by visually providing group consensus. 
    </p>
  `;

  return (
    <>
      {/* Prioritisation Intro */}
      <div className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/research">Research Tools</Link>
              &nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>
              &nbsp;&nbsp;
              <Link to="/problem">Problem</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/problem/prioritisation">Prioritisation</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={prioritisationIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Section Tools</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#priority-matrix">Priority Matrix</Link></div>
            <div><Link className="tool-section-cta" to="#swot-analysis">SWOT Analysis</Link></div>
            <div><Link className="tool-section-cta" to="#nuf-test">NUF Test</Link></div>
            <div><Link className="tool-section-cta" to="#priority-chart">Priority Chart</Link></div>
          </div>
        </div>
      </div>

      {/* Priority Matrix Section */}
      <main className="case-study">
        <span className="case-tool-title" id="priority-matrix">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="priority-matrix">
            <span className="tool-title-number">
              <Link to="#priority-matrix">Priority Matrix</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/problem">Problem</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/problem/prioritisation">Prioritisation</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#priority-matrix"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-priority-matrix" 
            id="tab-priority-matrix-overview" 
            defaultChecked
          />
          <label htmlFor="tab-priority-matrix-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              <div className="tool-table-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th colSpan="3">Tool Info</th>
                    </tr>
                    <tr>
                      <td>Category</td>
                      <td>Prioritisation</td>  
                    </tr>
                    <tr>
                      <td>Updated</td>
                      <td>20/08/20</td>
                    </tr>
                    <tr>
                      <td>Templates</td>
                      <td>
                        <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                        <a href="/problem/prioritisation/priority-matrix.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                        <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                        <a href="https://miro.com/app/board/o9J_kpPWRDY=/" className="link-table">Miro</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Creator</td>
                      <td><a href="https://en.wikipedia.org/wiki/Stephen_Covey">Stephen Covey</a></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="tool-opening">
                <p>
                  At its basic, a Priority Matrix is a 2x2 grid, or X/Y grid that encourages items to be plotted in a way that visually prioritises things.
                </p>
                <p>
                  In this instance we can use it to understand the value of solving a problem vs the effort or resource required, butit can be used a number of other ways including feature prioritisation.
                </p>
                <p>
                  If there is an imbalance of items, i.e. they are too heavily skewed towards a particular area, then you can move theX or Y axis in order to rebalance things.
                </p>
                <h4>Tips</h4>
                <ul className="bullet-tk">
                  <li>Try and consider the business impact of each item, the users, brand, revenue and efficiencies of each item should be considered.</li>
                  <li>Try and be fair with your plotting.</li>
                  <li>Encourage collaboration, the more diverse the better.</li>
                  <li>Always be zen and remove emotion.</li>
                </ul>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.mindtheproduct.com/enter-matrix-lean-prioritisation/" className="link-table">Enter The Matrix – Lean Prioritisation</a><br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://university.hygger.io/en/articles/2461512-value-vs-effort" className="link-table">Value vs Effort</a><br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/prioritization-matrices/" className="link-table">Using Prioritization Matrices to Inform UX Decisions</a><br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://uxdesign.cc/getting-your-priorities-right-11208586c1c7" className="link-table">Getting your product priorities right</a><br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.process.st/prioritization-matrix/" className="link-table">Prioritization Matrix 101: What, How & Why?</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-priority-matrix" 
            id="tab-priority-matrix-template"
          />
          <label htmlFor="tab-priority-matrix-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kpPWRDY=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Priority Matrix Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* SWOT Analysis Section */}
      <main className="case-study">
        <span className="case-tool-title" id="swot-analysis">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="swot-analysis">
            <span className="tool-title-number">
              <Link to="#swot-analysis">SWOT Analysis</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/problem">Problem</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/problem/prioritisation">Prioritisation</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#swot-analysis"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-swot-analysis" 
            id="tab-swot-analysis-overview" 
            defaultChecked
          />
          <label htmlFor="tab-swot-analysis-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              <div className="tool-table-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th colSpan="3">Tool Info</th>
                    </tr>
                    <tr>
                      <td>Category</td>
                      <td>Prioritisation</td>  
                    </tr>
                    <tr>
                      <td>Updated</td>
                      <td>10/08/20</td>
                    </tr>
                    <tr>
                      <td>Templates</td>
                      <td>
                        <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                        <a href="/problem/prioritisation/swot-analysis.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                        <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                        <a href="https://miro.com/app/board/o9J_kpd-ZfQ=/" className="link-table">Miro</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Creator</td>
                      <td><a href="https://www.bl.uk/business-and-ip-centre/articles/what-is-swot-analysis">Albert Humphrey</a></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="tool-opening">
                <p>
                  Better for an existing business or product where there is data and experience, using a SWOT analysis can help further focus what problems to prioritise whilst aligning them with business goals. 
                </p>
                <p>
                  <span className="underline">Strengths</span><br /> 
                  <ul className="bullet-tk">
                    <li>Things that the organisation is strong at.</li>
                    <li>Unique characteristics that differentiate the organisation.</li>
                    <li>Particular internal resources, for example the team, or IP.</li>
                  </ul>
      
                  <span className="underline">Weaknesses</span><br /> 
                  <ul className="bullet-tk">
                    <li>Things that competitors do better.</li>
                    <li>Poorly defined USP.</li>
                    <li>Limited Resources.</li>
                    <li>Things the organisation lack.</li>
                  </ul>
      
                  <span className="underline">Opportunities</span><br /> 
                  <ul className="bullet-tk">
                    <li>Markets that are underserved.</li>
                    <li>Lack of competition.</li>
                    <li>Media coverage of product or service.</li>
                    <li> Emerging market and need for product or service.</li>
                  </ul>
      
                  <span className="underline">Threats</span><br /> 
                  <ul className="bullet-tk">
                    <li>New competition.</li>
                    <li>Regulations changing.</li>
                    <li>Negative media coverage.</li>
                    <li>User attitudes shifting negatively.</li>
                  </ul>
                </p>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-swot-analysis" 
            id="tab-swot-analysis-template"
          />
          <label htmlFor="tab-swot-analysis-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kpd-ZfQ=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="SWOT Analysis Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* NUF Test Section */}
      <main className="case-study">
        <span className="case-tool-title" id="nuf-test">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="nuf-test">
            <span className="tool-title-number">
              <Link to="#nuf-test">NUF Test</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/problem">Problem</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/problem/prioritisation">Prioritisation</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#nuf-test"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-nuf-test" 
            id="tab-nuf-test-overview" 
            defaultChecked
          />
          <label htmlFor="tab-nuf-test-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              <div className="tool-table-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th colSpan="3">Tool Info</th>
                    </tr>
                    <tr>
                      <td>Category</td>
                      <td>Prioritisation</td>  
                    </tr>
                    <tr>
                      <td>Updated</td>
                      <td>20/08/20</td>
                    </tr>
                    <tr>
                      <td>Templates</td>
                      <td>
                        <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                        <a href="/problem/prioritisation/nuf-test.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                        <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                        <a href="https://miro.com/app/board/o9J_kpTbPcI=/" className="link-table">Miro</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Creator</td>
                      <td><a href="http://www.creativeeducationfoundation.org/facilitation_tool/5-ws-h/">Sakichi Toyoda</a></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="tool-opening">
                <p>
                  Adding some quantitative analysis when prioritising can really help with further understanding the item and whether it should be pursued further. An NUF test is useful as a lean method of adding this data.
                </p>
                <p>
                  Taking the item and rating it based on it's 'newness', 'usefulness' and 'feasibility' and then looking at the total score helps to crystalise the opportunity at hand.
                </p>
                <p>
                  The NUF test works especially well as part of a group prioritising session, helping to align a team and achieve group consensus.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://gamestorming.com/nuf-test/" className="link-table">NUF Test</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-nuf-test" 
            id="tab-nuf-test-template"
          />
          <label htmlFor="tab-nuf-test-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kpTbPcI=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="NUF Test Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Priority Chart Section */}
      <main className="case-study">
        <span className="case-tool-title" id="priority-chart">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="priority-chart">
            <span className="tool-title-number">
              <Link to="#priority-chart">Priority Chart</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/problem">Problem</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/problem/prioritisation">Prioritisation</Link>
          </div>
          <div className="tool-title-link">
            <Link to="#priority-chart"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-priority-chart" 
            id="tab-priority-chart-overview" 
            defaultChecked
          />
          <label htmlFor="tab-priority-chart-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              <div className="tool-table-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th colSpan="3">Tool Info</th>
                    </tr>
                    <tr>
                      <td>Category</td>
                      <td>Prioritisation</td>  
                    </tr>
                    <tr>
                      <td>Updated</td>
                      <td>20/08/20</td>
                    </tr>
                    <tr>
                      <td>Templates</td>
                      <td>
                        <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                        <a href="/problem/prioritisation/priority-chart.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                        <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                        <a href="https://miro.com/app/board/o9J_kpdLcxs=/" className="link-table">Miro</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Creator</td>
                      <td><a href="https://en.wikipedia.org/wiki/Stephen_Covey">Stephen Covey</a></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="tool-opening">
                <p>
                  Understanding how big an impact tackling a specific problem will have is valuable when prioritising problems.
                </p>
                <p>
                  A Priority Chart can help guide this by focusing on the Quick Wins, the Big Bets and how they align with effort.
                </p>
                <p>
                  This also helps with removing any problems that are 'time sinks' and have little value in comparison to the amount of resource required.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.prodpad.com/resources/guides/product-management-process/prioritization/" className="link-table">What is feature prioritization?</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-priority-chart" 
            id="tab-priority-chart-template"
          />
          <label htmlFor="tab-priority-chart-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kpdLcxs=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Priority Chart Template"
            ></iframe>
          </div>
        </div>
      </main>
    </>
  );
};

export default PrioritisationPage; 